import { useState, useMemo } from "react";
import PageHero from "@/components/shared/PageHero";
import PropertyCard from "@/components/shared/PropertyCard";
import { LoadingGrid, ErrorState, EmptyState } from "@/components/shared/DataState";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getListings } from "@/data/repository";
import { ASSETS, BRAND } from "@/lib/brand";
import { LISTING_CATEGORIES } from "@/lib/listingCategories";
import { ExternalLink } from "lucide-react";

const FILTERS = ["All", ...LISTING_CATEGORIES];
const PROPERTYGURU_URL =
  "https://www.propertyguru.com.sg/agent/pek-kiap-yong-13108886#Agent-RightSideProfile";

function PropertyGuruWordmark() {
  // Inline approximation of PropertyGuru's wordmark — house icon + brand color text
  return (
    <span className="inline-flex items-center gap-2 font-bold tracking-tight">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 11.5L12 3l9 8.5V21H14v-7h-4v7H3v-9.5z"
          fill="#ED1C24"
        />
      </svg>
      <span className="text-[#ED1C24]">PropertyGuru</span>
    </span>
  );
}

function PropertyGuruBanner() {
  return (
    <a
      href={PROPERTYGURU_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="propertyguru-cta"
      className="reveal flex flex-col md:flex-row md:items-center justify-between gap-5 border border-bronze/25 bg-cream p-6 md:p-8 hover:border-bronze transition-colors group"
    >
      <div className="flex items-center gap-5">
        <div className="hidden md:flex h-14 w-14 items-center justify-center bg-[#ED1C24]/10 rounded-full">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 11.5L12 3l9 8.5V21H14v-7h-4v7H3v-9.5z" fill="#ED1C24" />
          </svg>
        </div>
        <div>
          <div className="text-[0.65rem] tracking-[0.25em] uppercase text-olive mb-1">
            More live listings
          </div>
          <div className="font-serif text-2xl md:text-3xl text-ink leading-tight">
            Explore my active listings on <PropertyGuruWordmark />
          </div>
        </div>
      </div>
      <span className="inline-flex items-center gap-2 self-start md:self-auto px-5 py-3 bg-[#ED1C24] text-white text-[0.7rem] tracking-[0.18em] uppercase font-medium group-hover:bg-[#c41820] transition-colors whitespace-nowrap">
        View on PropertyGuru <ExternalLink size={14} />
      </span>
    </a>
  );
}

export default function ListingsPage() {
  const [filter, setFilter] = useState("All");
  const { data: listings, loading, error, reload } = useAsyncData(getListings, []);

  const filtered = useMemo(
    () => (filter === "All" ? listings : listings.filter((l) => l.category === filter)),
    [filter, listings]
  );

  // Counts for the category filter chips.
  const counts = useMemo(() => {
    const c = { All: listings.length };
    LISTING_CATEGORIES.forEach((cat) => {
      c[cat] = listings.filter((l) => l.category === cat).length;
    });
    return c;
  }, [listings]);

  // Status summary for the hero meta strip.
  const statusCounts = useMemo(
    () => ({
      All: listings.length,
      Available: listings.filter((l) => (l.listingStatus || l.status) === "Available").length,
      "Coming Soon": listings.filter((l) => (l.listingStatus || l.status) === "Coming Soon").length,
    }),
    [listings]
  );

  return (
    <div data-testid="page-listings">
      <PageHero
        eyebrow="Curated Listings"
        title={<>Spaces with <span className="italic text-bronze">stories</span> to tell.</>}
        sub="A small, hand-picked selection of homes — from quiet HDB gems to freehold sanctuaries. Browse, then let's talk."
        image={ASSETS.lifestyle.livingRoom}
        ctas={[
          { label: "Speak With Kiap", href: BRAND.whatsapp, variant: "primary" },
          { label: "Get Valuation", href: BRAND.valuationLink, variant: "secondary" },
        ]}
        meta={[
          { label: "Total Active", value: statusCounts.All },
          { label: "For Sale", value: statusCounts.Available },
          { label: "Off-market", value: statusCounts["Coming Soon"] },
        ]}
      />

      {/* PropertyGuru CTA strip */}
      <section className="bg-cream pt-8 md:pt-10">
        <div className="container-page">
          <PropertyGuruBanner />
        </div>
      </section>

      {/* Filter strip */}
      <section className="bg-beige/60 border-b border-bronze/15 mt-8">
        <div className="container-page py-6 flex flex-wrap items-center gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`listing-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className={`px-5 py-2.5 text-[0.7rem] tracking-[0.22em] uppercase border transition-colors ${
                filter === f
                  ? "bg-bronze text-cream border-bronze"
                  : "bg-cream border-bronze/30 text-ink_soft hover:border-bronze hover:text-bronze"
              }`}
            >
              {f} <span className="ml-1 opacity-60">({counts[f]})</span>
            </button>
          ))}
          <span className="ml-auto text-xs text-olive">
            Showing {filtered.length} of {listings.length}
          </span>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-cream">
        <div className="container-page">
          {loading && <LoadingGrid count={4} />}

          {!loading && error && <ErrorState onRetry={reload} />}

          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filtered.map((l, i) => (
                <PropertyCard key={l.id} listing={l} idx={i} />
              ))}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <EmptyState
              title="No listings in this category right now."
              sub="Reach out — new properties join the list often."
            />
          )}

          <div className="mt-16 p-10 md:p-14 bg-beige/70 border border-bronze/20 text-center">
            <p className="font-serif text-3xl md:text-4xl text-ink leading-snug">
              Don't see what you're looking for?
            </p>
            <p className="mt-3 text-ink_soft/75 max-w-xl mx-auto">
              Many of my best opportunities never reach a public portal. Let's talk about what you
              really want — and how to find it.
            </p>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listings-cta-whatsapp"
              className="btn-primary mt-7"
            >
              Speak With Kiap
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
